import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import {
    DesignTokens,
    GivelifyLabel,
    GivelifyLink,
    GivelifyButton,
} from '@givelify/ui';
import { webConfig } from 'webConfig';
import Logo from '../../components/Logo';
import { useInvokeApi } from '../../utils/useInvokeApi';

const Wrapper = styled('div')(() => ({
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
}));

const BorderedText = styled(GivelifyLabel)(({ theme }) => ({
    display: 'inline-block',
    borderRadius: 4,
    padding: theme.spacing(2, 4),
    color: '#E3E3E3',
    backgroundColor: '#323232',
    marginTop: 24,
}));

const GivelifyLogo = styled(Logo)(() => ({
    marginBottom: 24,
}));

const ErrorPage: React.FC<{ code?: number; email: string }> = ({
    code,
    email,
}) => {
    const { t } = useTranslation();
    const TRANSLATION_KEY = 'signup.activate.error';
    const scopedTranslate = useCallback(
        (key: string, prs?: any) => t(`${TRANSLATION_KEY}.${key}`, prs),
        [t],
    );

    const copy = useMemo(
        () => ({
            409: scopedTranslate('409'),
            410: scopedTranslate('410'),
            404: scopedTranslate('404'),
            422: scopedTranslate('422'),
            common: scopedTranslate('common'),
            tryAgain1: scopedTranslate('tryAgain1'),
            tryAgain2: scopedTranslate('tryAgain2'),
            tryAgain3: scopedTranslate('tryAgain3'),
            resendLoading: scopedTranslate('reend.loading'),
            resendSuccess: scopedTranslate('resend.success', { email }),
            continueToSignIn: scopedTranslate('continueToSignIn'),
            contactUs: scopedTranslate('contactUs'),
        }),
        [scopedTranslate, email],
    );

    const errorMessage = useMemo(
        () =>
            (code === 409 && copy['409']) ||
            (code === 410 && copy['410']) ||
            (code === 404 && copy['404']) ||
            (code === 422 && copy['422']) ||
            copy.common,
        [code, copy],
    );

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));

    const [resendStatus, makeResendRequest] = useInvokeApi();
    const onResend = () => {
        makeResendRequest('POST', '/signup/resend-verification', {
            email,
        });
    };

    const resendText = useMemo(() => {
        switch (resendStatus.type) {
            case 'REQUEST_START':
                return copy.resendLoading;
            case 'REQUEST_SUCCESS':
                return copy.resendSuccess;
            case 'REQUEST_ERROR':
                return resendStatus.error.message;
        }
    }, [resendStatus, copy.resendLoading, copy.resendSuccess]);

    return (
        <Wrapper data-testid={`error${code}`}>
            <GivelifyLogo />
            <GivelifyLabel
                variant={isMobile ? 'body2B' : 'body1B'}
                text={errorMessage}
                style={{ marginBottom: 24 }}
                color={DesignTokens.color.textSecondary}
            />
            {code === 409 ? (
                <GivelifyButton
                    variant="primary"
                    text={copy.continueToSignIn}
                    href={webConfig.givelifySignInPageUrl}
                />
            ) : code === 410 ? (
                <>
                    <GivelifyLabel
                        variant={isMobile ? 'body2B' : 'body1B'}
                        color={DesignTokens.color.textSecondary}
                    >
                        {copy.tryAgain1}{' '}
                        <GivelifyLink
                            underline="hover"
                            text={copy.tryAgain2}
                            onClick={onResend}
                        />
                        {copy.tryAgain3}
                    </GivelifyLabel>
                    {!!resendText && (
                        <BorderedText
                            variant={isMobile ? 'body2B' : 'body1B'}
                            text={resendText}
                            style={{
                                visibility: code !== 410 ? 'hidden' : undefined,
                            }}
                            data-testid={`resend_${resendStatus.type}`}
                        />
                    )}
                </>
            ) : (
                <GivelifyButton
                    variant="primary"
                    text={copy.contactUs}
                    href={webConfig.givelifySupportUrl}
                />
            )}
        </Wrapper>
    );
};

export default ErrorPage;
